import { toast } from "@aurumiq/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { errorMessage, qs, rawApi } from "@/lib/api";
import type {
  LatestRelease,
  Paginated,
  Release,
  ReleaseChannel,
  ReleaseCreateInput,
  ReleasePatchInput,
} from "@/lib/types";

const BASE = "/api/v1/admin";

export const releaseKeys = {
  all: ["admin", "releases"] as const,
  list: (channel: ReleaseChannel | "", page: number) =>
    ["admin", "releases", { channel, page }] as const,
  latest: (channel: ReleaseChannel) => ["public", "releases", "latest", channel] as const,
};

export function useReleases(channel: ReleaseChannel | "" = "", page = 1) {
  return useQuery({
    queryKey: releaseKeys.list(channel, page),
    queryFn: () => rawApi.get<Paginated<Release>>(`${BASE}/releases${qs({ channel, page })}`),
  });
}

/** There is no detail endpoint yet: resolve a version from the unfiltered list. */
export function useRelease(version: string) {
  const list = useReleases("", 1);
  return {
    ...list,
    release: list.data?.items.find((r) => r.version === version) ?? null,
  };
}

export function useLatestRelease(channel: ReleaseChannel) {
  return useQuery({
    queryKey: releaseKeys.latest(channel),
    retry: false,
    queryFn: () => rawApi.get<LatestRelease>(`/api/v1/public/releases/latest${qs({ channel })}`),
  });
}

function useReleaseMutation<TVars, TData = Release>(
  fn: (vars: TVars) => Promise<TData>,
  success: string | ((data: TData, vars: TVars) => string),
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: releaseKeys.all });
      qc.invalidateQueries({ queryKey: ["public", "releases"] });
      toast.success(typeof success === "function" ? success(data, vars) : success);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
}

export function useCreateRelease() {
  return useReleaseMutation(
    (input: ReleaseCreateInput) => rawApi.post<Release>(`${BASE}/releases`, input),
    (r) => `Draft ${r.version} created`,
  );
}

export function usePatchRelease() {
  return useReleaseMutation(
    ({ id, ...input }: ReleasePatchInput & { id: string }) =>
      rawApi.patch<Release>(`${BASE}/releases/${id}`, input),
    "Release updated",
  );
}

export function usePublishRelease() {
  return useReleaseMutation(
    ({ id, reason }: { id: string; reason?: string }) =>
      rawApi.post<Release>(`${BASE}/releases/${id}/publish`, { reason }),
    (r) => `${r.version} published`,
  );
}

export function useYankRelease() {
  return useReleaseMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      rawApi.post<Release>(`${BASE}/releases/${id}/yank`, { reason }),
    (r) => `${r.version} yanked`,
  );
}

export function usePromoteRelease() {
  return useReleaseMutation(
    ({ id, reason }: { id: string; reason?: string }) =>
      rawApi.post<Release>(`${BASE}/releases/${id}/promote`, { to_channel: "stable", reason }),
    (r) => `${r.version} promoted to stable`,
  );
}
